import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { Download, Loader2, Search, CheckCircle2, XCircle, Filter } from 'lucide-react';

import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/data-table/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuditLogs, useExportAuditCsv, type AuditLog } from '@/hooks/use-audit';
import { absoluteTime, relativeTime, shortId } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api-client';

export const Route = createFileRoute('/_app/audit')({
  component: AuditPage,
});

function AuditPage() {
  const [page, setPage] = React.useState(1);
  const [action, setAction] = React.useState('');
  const [debouncedAction, setDebouncedAction] = React.useState('');
  const [targetType, setTargetType] = React.useState<string>('all');
  const [success, setSuccess] = React.useState<'all' | 'true' | 'false'>('all');
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const [expanded, setExpanded] = React.useState<string | null>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedAction(action), 250);
    return () => clearTimeout(t);
  }, [action]);

  const filters = {
    page,
    pageSize: 50,
    action: debouncedAction || undefined,
    targetType: targetType === 'all' ? undefined : targetType,
    success: success === 'all' ? undefined : success === 'true',
    fromDate: fromDate ? new Date(fromDate).toISOString() : undefined,
    toDate: toDate ? new Date(toDate).toISOString() : undefined,
  };

  const { data, isLoading } = useAuditLogs(filters);
  const exportCsv = useExportAuditCsv();

  function handleExport(): void {
    exportCsv.mutate(filters, {
      onSuccess: () => toast({ title: 'Export started', description: 'CSV is being downloaded.' }),
      onError: (err) =>
        toast({
          variant: 'destructive',
          title: 'Export failed',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
    });
  }

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every state-changing action across the organization, with actor, target, and metadata."
        actions={
          <Button variant="outline" onClick={handleExport} disabled={exportCsv.isPending}>
            {exportCsv.isPending ? <Loader2 className="animate-spin" /> : <Download />}
            <span>Export CSV</span>
          </Button>
        }
      />

      <Card className="mb-4 p-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="space-y-1.5">
            <Label className="text-xs">Action</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="user.create, device.update…"
                value={action}
                onChange={(e) => { setAction(e.target.value); setPage(1); }}
                className="pl-8"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Target type</Label>
            <Select value={targetType} onValueChange={(v) => { setTargetType(v); setPage(1); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="group">Group</SelectItem>
                <SelectItem value="device">Device</SelectItem>
                <SelectItem value="session">Session</SelectItem>
                <SelectItem value="organization">Organization</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Outcome</Label>
            <Select value={success} onValueChange={(v: 'all' | 'true' | 'false') => { setSuccess(v); setPage(1); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Success</SelectItem>
                <SelectItem value="false">Failure</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">From</Label>
            <Input
              type="datetime-local"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">To</Label>
            <Input
              type="datetime-local"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            />
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Time</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Target</TableHead>
              <TableHead className="w-24">Outcome</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5} className="h-32 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                <Filter className="mx-auto h-5 w-5 mb-2 opacity-50" />
                No audit entries match the current filters.
              </TableCell></TableRow>
            ) : (
              data?.data.map((log) => (
                <AuditRow
                  key={log.id}
                  log={log}
                  expanded={expanded === log.id}
                  onToggle={() => setExpanded(expanded === log.id ? null : log.id)}
                />
              ))
            )}
          </TableBody>
        </Table>
        {data && (
          <div className="border-t">
            <Pagination page={page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </>
  );
}

function AuditRow({ log, expanded, onToggle }: { log: AuditLog; expanded: boolean; onToggle: () => void }) {
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="font-mono text-xs tabular-nums">
          <div className="flex flex-col">
            <span>{absoluteTime(log.timestamp)}</span>
            <span className="text-2xs text-muted-foreground">{relativeTime(log.timestamp)}</span>
          </div>
        </TableCell>
        <TableCell>
          <span className="font-mono text-xs">{log.action}</span>
        </TableCell>
        <TableCell>
          {log.actor ? (
            <div className="flex flex-col">
              <span className="text-sm">{log.actor.email}</span>
              <span className="text-2xs text-muted-foreground">{log.actorType}</span>
            </div>
          ) : (
            <Badge variant="outline" className="font-mono">{log.actorType}</Badge>
          )}
        </TableCell>
        <TableCell>
          {log.targetType && (
            <div className="flex items-center gap-1.5">
              <Badge variant="secondary" className="font-mono">{log.targetType}</Badge>
              {log.targetId && <span className="id-pill">{shortId(log.targetId)}</span>}
            </div>
          )}
        </TableCell>
        <TableCell>
          {log.success ? (
            <span className="inline-flex items-center gap-1 text-success">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span className="text-xs">OK</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-destructive">
              <XCircle className="h-3.5 w-3.5" />
              <span className="text-xs">Failed</span>
            </span>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/30">
            <div className="space-y-2 text-xs font-mono">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">id</span> {log.id}</div>
                <div><span className="text-muted-foreground">ip</span> {log.ipAddress ?? '—'}</div>
                {log.errorMsg && (
                  <div className="col-span-2 text-destructive">
                    <span className="text-muted-foreground">error</span> {log.errorMsg}
                  </div>
                )}
              </div>
              {log.metadata !== null && log.metadata !== undefined && (
                <details className="rounded border bg-background p-2">
                  <summary className="cursor-pointer text-muted-foreground">metadata</summary>
                  <pre className="mt-2 overflow-auto text-2xs">
                    {JSON.stringify(log.metadata, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
